import axios from 'axios';

const apiUrl = 'https://api.perplexity.ai/chat/completions';

export async function callSonar(prompt, context = {}, apiKey, model = 'sonar') {
  try {
    const response = await axios.post(
      apiUrl,
      {
        messages: [{ role: "user", content: prompt }],
        model: model,
        ...context
      },
      {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error calling Sonar API:', error);
    throw error;
  }
}

export async function generateExplanation(topic, level, apiKey) {
  const prompt = `Explain "${topic}" at comprehension level ${level}, 
                  where 1 is for a 5-year-old and 5 is PhD-level understanding.

                  For level 1 (5-year-old): Use simple words, short sentences, concrete examples, and colorful metaphors.
                  For level 2 (10-year-old): Use basic concepts, longer sentences, some technical terms with explanations.
                  For level 3 (16-year-old): Use more technical terms, abstract concepts, and detailed examples.
                  For level 4 (College Level): Use advanced concepts, field-specific terminology, and theoretical frameworks.
                  For level 5 (Expert Level): Use full technical vocabulary, nuanced analysis, and specialized knowledge.
                  
                  Explain "${topic}" at comprehension level ${level}
                  Format the explanation in easy-to-read paragraphs with simple headings. Use markdown only where needed. Avoid adding citations.`;
  
  let model = 'sonar-pro';
  if (level === 5) {
    console.log(`level ${level}`);
    model = 'sonar-deep-research';
  }

  const result = await callSonar(prompt, {}, apiKey, model);
  let content = result.choices[0].message.content;
  content = content.replace(/<think>[\s\S]*?<\/think>/g, '');
  return content;
}

export async function generateMetaphors(topic, apiKey) {
  const prompt = `Create 5 visual metaphors that help explain "${topic}" 
                  with a title and brief description for each.
                  
                  Format each metaphor as a JSON object with 'title' and 'description' properties.
                  Return an array of these 5 metaphor objects.`;
  
  const result = await callSonar(prompt, {}, apiKey, 'sonar');
  try {
    // Parse the JSON array from the response text
    const response = result.choices[0].message.content;
    const jsonMatch = response.match(/(\{[\s\S]*\}|\[[\s\S]*\])/);
    if (!jsonMatch) {
      throw new Error('No JSON object found in response');
    }
    return JSON.parse(jsonMatch[0]);
  } catch (e) {
    // Fallback if the response isn't valid JSON
    console.error("Failed to parse JSON response:", e);
    return [{
      title: "Error parsing metaphors",
      description: result.choices[0].message.content
    }];
  }
}

export async function generateQuiz(topic, level, apiKey) {
  const prompt = `Create 5 multiple-choice questions to test understanding 
                  of "${topic}" at complexity level ${level}/5, where

                  1 (5-year-old)
                  2 (10-year-old)
                  3 (16-year-old)
                  4 (College Level)
                  5 (Expert or Ph.D Level)
                  
                  Format each question as a JSON object with:
                  - 'question': the question text
                  - 'options': array of 3-4 possible answers
                  - 'correct': the index of the correct answer (0-based)
                  
                  Return an array of these 5 question objects.`;
  
  const result = await callSonar(prompt, {}, apiKey, 'sonar-pro');
  try {
    // return JSON.parse(result.choices[0].message.content);
    const response = result.choices[0].message.content;
    const jsonMatch = response.match(/(\[[\s\S]*\])/);
    if (!jsonMatch) {
      throw new Error('No JSON object found in response');
    }
    return JSON.parse(jsonMatch[0]);
  } catch (e) {
    console.error("Failed to parse JSON response:", e);
    return [{
      question: "Error parsing quiz questions",
      options: ["Try again", "Contact support", "Refresh page"],
      correct: 0
    }];
  }
}

export async function generateLearningPath(topic, level, apiKey) {
  const prompt = `Create a 5-step learning path for someone wanting to understand "${topic}" 
                  at complexity level ${level}, where

                  level 1 (5-year-old)
                  level 2 (10-year-old)
                  level 3 (16-year-old)
                  level 4 (College Level)
                  level 5 (Expert or Ph.D Level)
                  
                  Format as a JSON array with 5 objects, each containing:
                  - 'step': number (1-5)
                  - 'title': short descriptive title
                  - 'description': brief explanation
                  - 'resources': suggested type of resources (books, videos, etc.) with reference links

                  Example:
                  [
                      {
                          "step": 1,
                          "title": "Foundational Concepts",
                          "description": "Introduce basic climate science, greenhouse effect mechanisms, and historical climate trends. Establish understanding of human vs natural contributors to global warming.",
                          "resources": [
                              {
                                  "name": "Interactive courses (UN CC:Learn)",
                                  "link": "https://unccelearn.org/course/view.php?id=207"
                              },
                              {
                                  "name": "Animated explainer videos (YouTube)",
                                  "link": "https://youtube.com/username/videoid"
                              }
                          ]
                      }
                  ]
                  `;
  
  const result = await callSonar(prompt, {}, apiKey, 'sonar-deep-research');
  try {
    console.log(result.choices[0].message.content);
    let response = result.choices[0].message.content;
    response = response.replace(/<think>[\s\S]*?<\/think>/g, '');
    const jsonMatch = response.match(/(\{[\s\S]*\}|\[[\s\S]*\])/);
    if (!jsonMatch) {
      throw new Error('No JSON object found in response');
    }
    return JSON.parse(jsonMatch[0]);
  } catch (e) {
    console.error("Failed to parse JSON response:", e);
    return Array(5).fill(0).map((_, i) => ({
      step: i+1,
      title: `Step ${i+1}`,
      description: "Error generating learning path. Please try again.",
      resources: []
    }));
  }
}